/**
 * キャンペーン検索のクエリパラメータ関連ユーティリティ
 */

import { filterAllowedCampaignEntryActionTypes } from '@/common/utils/campaign_entry_action_rule'
import {
  CampaignStatusTypeSchema,
  CampaignTargetSnsType,
  CampaignTargetSnsTypeSchema,
  CampaignType,
  CampaignTypeSchema
} from '@/typings/campaigns'

export interface CampaignQueryParams {
  page?: string
  limit?: string
  title?: string
  status?: string
  targetSns?: string
  campaignAccountId?: string
  startDatetime?: string
  endDatetime?: string
  sortBy?: string
  groupId?: string
  campaignType?: string
  entryActionTypes?: string
}

export interface CampaignApiParams {
  pagingNo: number
  limitCount: number
  title?: string
  status?: string
  targetSns?: string
  campaignAccountId?: string
  startDatetime?: string
  endDatetime?: string
  sortBy: string
  groupId: number
  campaignType?: CampaignType
  entryActionTypes?: string[]
}

/**
 * entryActionTypes のCSV文字列(URLクエリ由来)をパースし、targetSns × campaignType に対して
 * 実際に許可された entryActionType の生値配列に絞り込む。
 */
export function parseAllowedEntryActionTypes(
  targetSns: CampaignTargetSnsType | undefined,
  campaignType: CampaignType | undefined,
  rawEntryActionTypes: string | undefined
): string[] {
  const entryActionTypes = rawEntryActionTypes ? rawEntryActionTypes.split(',').filter(s => s.length > 0) : []
  return filterAllowedCampaignEntryActionTypes(targetSns, campaignType, entryActionTypes)
}

/**
 * 日付範囲の妥当性をチェック（6ヶ月以内かどうか）
 */
export function isValidDateRange(startDatetime?: string, endDatetime?: string): boolean {
  if (!startDatetime || !endDatetime) return true

  try {
    // YYYY-MM-DD-HH-mm 形式から Date オブジェクトに変換
    const parseDateTime = (datetime: string) => {
      const match = datetime.match(/^(\d{4})-(\d{2})-(\d{2})-(\d{2})-(\d{2})$/)
      if (!match) return null
      const [, year, month, day, hour, minute] = match
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute))
    }

    const startDate = parseDateTime(startDatetime)
    const endDate = parseDateTime(endDatetime)

    if (!startDate || !endDate) return false

    // 終了日時が開始日時より後かチェック
    if (endDate <= startDate) return false

    // 6ヶ月後の日付を計算（zodバリデーションと同じロジック）
    const sixMonthsLater = new Date(startDate)
    sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6)

    return endDate <= sixMonthsLater
  } catch {
    return false
  }
}

/**
 * URLクエリパラメータをAPIパラメータに変換
 * @param query クエリパラメータ
 * @param groupId グループID
 * @param total 総レコード数（ページ数上限の計算用）
 */
export function queryParamsToApiParams(query: CampaignQueryParams, groupId: number, total?: number): CampaignApiParams {
  const convertDatetimeForApi = (datetime?: string) => {
    if (!datetime) return undefined
    // YYYY-MM-DD-HH-mm → YYYY-MM-DD HH:mm:ss 形式に変換
    return datetime.replace(/-(\d{2})-(\d{2})$/, ' $1:$2:00')
  }

  // リミット数のバリデーション（10, 25, 50のみ許可）
  let limitCount = 25
  if (query.limit) {
    const limitNum = parseInt(query.limit, 10)
    if (!isNaN(limitNum) && [10, 25, 50].includes(limitNum)) {
      limitCount = limitNum
    }
  }

  // ページ番号のバリデーション
  let pagingNo = 1
  if (query.page) {
    const pageNum = parseInt(query.page, 10)
    if (!isNaN(pageNum) && pageNum > 0) {
      // totalが指定されており、かつ0より大きい場合は実際の最大ページ数を計算
      if (total !== undefined && total > 0) {
        const maxPage = Math.ceil(total / limitCount)
        if (pageNum <= maxPage) {
          pagingNo = pageNum
        }
        // 範囲超過の場合は1ページ目に戻す（pagingNo = 1のまま）
      } else {
        pagingNo = pageNum
      }
    }
  }

  // 日付範囲のバリデーション
  let startDatetime = convertDatetimeForApi(query.startDatetime)
  let endDatetime = convertDatetimeForApi(query.endDatetime)

  if (!isValidDateRange(query.startDatetime, query.endDatetime)) {
    startDatetime = undefined
    endDatetime = undefined
  }

  const campaignTypeResult = CampaignTypeSchema.safeParse(query.campaignType)
  const campaignType = campaignTypeResult.success ? campaignTypeResult.data : undefined

  const targetSnsResult = CampaignTargetSnsTypeSchema.safeParse(query.targetSns)
  const targetSnsForFilter = targetSnsResult.success ? targetSnsResult.data : undefined
  const validEntryActionTypes = parseAllowedEntryActionTypes(targetSnsForFilter, campaignType, query.entryActionTypes)
  const entryActionTypes = validEntryActionTypes.length > 0 ? validEntryActionTypes : undefined

  return {
    pagingNo,
    limitCount,
    title: query.title || undefined,
    status: query.status || undefined,
    targetSns: query.targetSns || undefined,
    campaignAccountId: query.campaignAccountId || undefined,
    startDatetime,
    endDatetime,
    sortBy: query.sortBy || '-status',
    groupId,
    campaignType,
    entryActionTypes
  }
}

/**
 * 検索条件があるかどうかを判定（page、limit、sortBy以外）
 * 6ヶ月を超える日付範囲は検索条件として認識しない
 */
export function hasSearchConditions(query: CampaignQueryParams): boolean {
  // 日付範囲の検証
  let hasValidDateRange = false
  if (query.startDatetime || query.endDatetime) {
    // 片方のみの場合は有効
    if (!query.startDatetime || !query.endDatetime) {
      hasValidDateRange = true
    } else {
      // 両方ある場合は6ヶ月以内かチェック
      hasValidDateRange = isValidDateRange(query.startDatetime, query.endDatetime)
    }
  }

  return !!(
    query.title ||
    query.status ||
    query.targetSns ||
    query.campaignAccountId ||
    hasValidDateRange ||
    query.campaignType ||
    query.entryActionTypes
  )
}

/**
 * クエリパラメータのエラーパターンを検出し、修正されたパラメータを返す
 * @param query 元のクエリパラメータ
 * @param total 総レコード数（ページ数上限の計算用）
 * @param availableAccountIds 利用可能なアカウントIDの配列（オプション）
 * @returns { correctedQuery: 修正後のクエリパラメータ, hasErrors: エラーがあったかどうか }
 */
export function validateAndCorrectQueryParams(
  query: CampaignQueryParams,
  total?: number,
  availableAccountIds?: string[]
): { correctedQuery: CampaignQueryParams; hasErrors: boolean } {
  const correctedQuery: CampaignQueryParams = { ...query }
  let hasErrors = false

  // ページ番号のエラーチェック
  if (query.page) {
    const pageNum = parseInt(query.page, 10)
    if (isNaN(pageNum) || pageNum <= 0) {
      // 不正なページ番号
      delete correctedQuery.page
      hasErrors = true
    } else if (total !== undefined && total > 0) {
      // limitを取得してページ数上限をチェック
      const limitNum = query.limit ? parseInt(query.limit, 10) : 25
      const validLimit = !isNaN(limitNum) && [10, 25, 50].includes(limitNum) ? limitNum : 25
      const maxPage = Math.ceil(total / validLimit)
      if (pageNum > maxPage) {
        // ページ数範囲超過
        delete correctedQuery.page
        hasErrors = true
      }
    }
  }

  // リミット数のエラーチェック
  if (query.limit) {
    const limitNum = parseInt(query.limit, 10)
    if (isNaN(limitNum) || ![10, 25, 50].includes(limitNum)) {
      // 不正なリミット数
      delete correctedQuery.limit
      hasErrors = true
    }
  }

  // 日付範囲のエラーチェック（6ヶ月制限）
  if (query.startDatetime || query.endDatetime) {
    if (!isValidDateRange(query.startDatetime, query.endDatetime)) {
      // 不正な日付範囲
      delete correctedQuery.startDatetime
      delete correctedQuery.endDatetime
      hasErrors = true
    }
  }

  // SNSタイプのエラーチェック
  if (query.targetSns !== undefined && !CampaignTargetSnsTypeSchema.safeParse(query.targetSns).success) {
    // 不正なSNSタイプ
    delete correctedQuery.targetSns
    hasErrors = true
  }

  // ステータスのエラーチェック
  if (query.status !== undefined && !CampaignStatusTypeSchema.safeParse(query.status).success) {
    // 不正なステータス
    delete correctedQuery.status
    hasErrors = true
  }

  // アカウントIDのエラーチェック（利用可能なアカウントIDが提供されている場合のみ）
  if (query.campaignAccountId !== undefined && availableAccountIds && availableAccountIds.length > 0) {
    if (query.campaignAccountId === '' || !availableAccountIds.includes(query.campaignAccountId)) {
      // 存在しないアカウントID
      delete correctedQuery.campaignAccountId
      hasErrors = true
    }
  }

  // sortByのエラーチェック（基本的なバリデーション）
  if (query.sortBy) {
    const validSortOptions = ['-status', 'status', 'title', '-title', 'startDatetime', '-startDatetime']
    if (!validSortOptions.includes(query.sortBy)) {
      // 不正なソート条件
      delete correctedQuery.sortBy
      hasErrors = true
    }
  }

  // campaignType のエラーチェック
  if (query.campaignType !== undefined && !CampaignTypeSchema.safeParse(query.campaignType).success) {
    delete correctedQuery.campaignType
    hasErrors = true
  }

  // entryActionTypes のエラーチェック
  if (query.entryActionTypes !== undefined) {
    if (query.entryActionTypes === '') {
      delete correctedQuery.entryActionTypes
      hasErrors = true
    } else {
      const targetSnsResult = CampaignTargetSnsTypeSchema.safeParse(correctedQuery.targetSns)
      const campaignTypeResult = CampaignTypeSchema.safeParse(correctedQuery.campaignType)
      const validEntryActionKeys = parseAllowedEntryActionTypes(
        targetSnsResult.success ? targetSnsResult.data : undefined,
        campaignTypeResult.success ? campaignTypeResult.data : undefined,
        query.entryActionTypes
      )
      const entryActionKeys = query.entryActionTypes.split(',').filter(s => s.length > 0)
      if (validEntryActionKeys.length === 0) {
        // 無効分を除去した結果、有効な値が1つも残らない場合はキー自体を削除する
        delete correctedQuery.entryActionTypes
        hasErrors = true
      } else if (validEntryActionKeys.length !== entryActionKeys.length) {
        // 無効分のみを除去し、有効な値は残す
        correctedQuery.entryActionTypes = validEntryActionKeys.join(',')
        hasErrors = true
      }
    }
  }

  return { correctedQuery, hasErrors }
}
